import Head from "next/head";
import { useRouter } from "next/router";
import Welcome30Banner from "../../public/images/welcome50-1.png";
import Welcome30BannerMobile from "../../public/images/welcome50-m-new.png";
import useWindowSize from 'hooks/useWindowSize';
import { useMemo } from "react";
import Link from "next/link";
import { GetStaticProps } from "next/types";
import { getSeoMetaTags } from "utils/common.utils";
import { SeoMetaTags } from "constants/dataTypes";
import GlobalSeoMetaTags from "components/GlobalSeoMetaTags/GlobalSeoMetaTags";
import { BackgroundColor } from "utils/image.utils";

const MOBILE_VIEWPORT_WIDTH = 768;

export default function Welcome50({
    seoMetaTags
}: {
    seoMetaTags: SeoMetaTags;
}) {

    const { width } = useWindowSize();

    const router = useRouter();
    const isMobile = useMemo(
        () => width <= MOBILE_VIEWPORT_WIDTH,
        [width]
    );
    const links: any = {
        'au': '/shop?filters=Prescription+Glasses%2CBlue+Light+Filter+Glasses%2CPrescription+Sunglasses%2CNon-prescription+Sunglasses%2CFrames%2CArms%2CAccessories%2CReading+Glasses%2CNon+Prescription+Reading+Glasses',
        'nz': '/shop?filters=Prescription+Glasses%2CBlue+Light+Filter+Glasses%2CPrescription+Sunglasses%2CNon-prescription+Sunglasses%2CFrames%2CArms%2CAccessories%2CReading+Glasses%2CNon+Prescription+Reading+Glasses',
        'ca': '/shop?filters=Prescription+Glasses%2CBlue+Light+Filter+Glasses%2CPrescription+Sunglasses%2CNon-prescription+Sunglasses%2CFrames%2CArms%2CAccessories%2CReading+Glasses%2CNon+Prescription+Reading+Glasses',
        'international': '/shop?filters=Prescription+Glasses%2CBlue+Light+Filter+Glasses%2CPrescription+Sunglasses%2CNon-prescription+Sunglasses%2CFrames%2CArms%2CAccessories%2CReading+Glasses%2CNon+Prescription+Reading+Glasses',
    }

    var mobileStyle = {
        backgroundImage: `url(${Welcome30BannerMobile.src})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center top 50px', // Changed from 'top' to 'center top 100px'
        backgroundColor: '#C6BDAE'
    }

    var desktopStyle = {
        backgroundImage: `url(${Welcome30Banner.src})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundColor: '#C6BDAE'
    }

    const price = '$50';
    const metaValuesByRegion: any = {
        au: {
            title: 'Get 50% Off Your First Order',
            description: 'Limited time only, glasses from $60 includes premium lenses & frames. Register today and get your 50% off on your first purchase!',
        },
        ca: {
            title: 'Get 50% Off Your First Order',
            description: 'Limited time only, glasses from $45 includes premium lenses & frames. Register today and get your 50% off on your first purchase!',
        },
        nz: {
            title: 'Get 50% Off Your First Order',
            description: 'Limited time only, glasses from $60 includes premium lenses & frames. Register today and get your 50% off on your first purchase!',
        },
        international: {
            title: 'Get 50% Off Your First Order',
            description: 'Limited time only, glasses from $60 includes premium lenses & frames. Register today and get your 50% off on your first purchase!',
        }
    }

    return <>
        <Head>
            <title>{metaValuesByRegion[router.locale as string].title} | Dresden Vision</title>
            <meta name="twitter:title" content={metaValuesByRegion[router.locale as string].title} />
            <meta name="og:title" content={metaValuesByRegion[router.locale as string].title} />
            <meta name="description" content={metaValuesByRegion[router.locale as string].description} />
            <meta property="og:description" content={metaValuesByRegion[router.locale as string].description} />
            <meta name="twitter:description" content={metaValuesByRegion[router.locale as string].description} />
        </Head>

        <section className="xsm:mb-[0px] md:h-[700px] xsm:h-[1100px] md:mb-[0px]"
            style={
                isMobile ? mobileStyle : desktopStyle
            }>

            <div className="mx-auto flex max-w-[1224px] ">
                <div className="absolute flex flex-col md:w-[677px] md:h-[715px] xsm:h-[600px] xsm:ml-[12px] xsm:mr-[12px]">
                    <>
                        {isMobile ? (
                            <p className="text-white font-semibold md:text-[40px] xsm:text-[35px] leading-[44px] md:mt-[45px] xsm:mt-[25px] xsm:text-center md:text-left text-[#054675]">
                                50% Off Your First Order
                                Prescription glasses from $50
                            </p>
                        ) : (
                            <p className="text-white font-semibold md:text-[40px] xsm:text-[35px] leading-[44px] md:mt-[45px] xsm:mt-[25px] xsm:text-center md:text-left text-[#054675]">
                                50% Off Your First Order <br />
                                Prescription glasses from $50
                            </p>
                        )}

                        {isMobile ? (
                            <p className="text-white font-semibold md:text-[18px] md:leading-[27.5px] md:leading-[18px] xsm:text-[17px] md:mt-[33px] xsm:text-center xsm:mt-[20px] md:text-left text-[#054675]">
                                The 90’s called.<br></br> They want their prices back.
                            </p>
                        ) : (
                            <p className="text-white font-semibold md:text-[18px] md:leading-[27.5px] md:leading-[18px] xsm:text-[17px] md:mt-[33px] xsm:text-center xsm:mt-[136px] md:text-left text-[#054675]">
                                The 90’s called. They want their prices back.
                            </p>
                        )}

                        {((router.locale == 'au' || router.locale == 'ca') && !isMobile) && (
                            <p className="text-white font-normal text-[18px] leading-[22px] text-[#054675] md:mt-[33px] xsm:text-center xsm:mt-[300px] md:block md:text-left">
                                Create your account online, over the phone or in-store<br></br>
                                and then apply code Welcome50 at checkout. Includes single vision, {
                                    router.locale == 'ca' ? 'progressives' : 'multifocals'
                                }, non-prescription glasses, extra frames and Dresden branded accessories (Contact lenses and Non-Dresden accessories not included).<br></br>
                                Enter code:<span className="font-medium"> Welcome50</span> at checkout.
                            </p>
                        )}

                        {((router.locale == 'nz' || router.locale == 'international') && !isMobile) && (
                            <p className="text-white font-normal text-[18px] leading-[22px] text-[#054675] md:mt-[33px] xsm:text-center xsm:mt-[300px] md:block md:text-left">
                                Create your account online, over the phone or in-store<br></br>
                                and then apply code Welcome50 at checkout. Includes single vision, {
                                    router.locale == 'nz' ? 'progressives' : 'multifocals'
                                }, non-prescription glasses, extra frames and Dresden branded accessories (Non-Dresden accessories not included).<br></br>
                                Enter code:<span className="font-medium"> Welcome50</span> at checkout.
                            </p>
                        )}

                        {((router.locale == 'au' || router.locale == 'ca') && isMobile) && (
                            <p className="text-white font-normal text-[18px] leading-[22px] text-[#054675] md:mt-[33px] xsm:text-center xsm:mt-[300px] md:block md:text-left">
                                Create your account online, over the phone or in-store<br></br>
                                and then apply code Welcome50 at checkout. Includes single vision, multifocals, non-prescription glasses, extra frames and Dresden branded accessories (Content lenses and Non-Dresden accessories not included).<br></br>
                                Enter code:<span className="font-medium"> Welcome50</span> at checkout.
                            </p>
                        )}

                        {((router.locale == 'nz' || router.locale == 'international') && isMobile) && (
                            <p className="text-white font-normal text-[18px] leading-[22px] text-[#054675] md:mt-[33px] xsm:text-center xsm:mt-[330px] md:block md:text-left">
                                Create your account online, <br></br>over the phone or in-store<br></br>
                                and then apply code Welcome50<br></br> at checkout. Includes single vision, multifocals, non-prescription glasses,<br></br> extra frames and Dresden branded accessories (Non-Dresden accessories not included).<br></br>
                                Enter code:<span className="font-medium"> Welcome50</span> at checkout.
                            </p>
                        )}


                        {isMobile && (
                            <>
                                <div className="flex xsm:flex-col md:flex-row md:mt-5 xsm:mt-[10px] md:justify-start md:items-start xsm:justify-center xsm:items-center">
                                    <Link
                                        prefetch={false}
                                        href={links[router.locale as any]}
                                        className="text-[20px] font-medium leading-[18px] text-[#fff]">

                                        <button className="bg-[#054675] font-semibold border-2 border-solid border-[#054675] md:w-[391px] xsm:w-[320px] py-4 mt-[20px] xsm:border-[#1c499e] xsm:border-1 rounded-[6px] shadow">
                                            {`Get Shoppin’`}
                                        </button>
                                    </Link>
                                </div>
                            </>
                        )}

                        {!isMobile && (
                            <div className="flex xsm:flex-col md:flex-row md:mt-[40px] xsm:mt-[0px] md:justify-start md:items-start xsm:justify-center xsm:items-center">
                                <Link
                                    prefetch={false}
                                    href={links[router.locale as any]}
                                    className="text-[20px] font-medium leading-[18px] text-[#fff]">

                                    <button className="bg-[#054675] font-semibold border-2 border-solid border-[#054675] md:w-[320px] xsm:w-[250px] py-4 mt-[20px] xsm:border-[#1c499e] xsm:border-1 rounded-[6px] shadow">
                                        Shop Now
                                    </button>

                                </Link>
                            </div>
                        )}

                        {(!isMobile) && (
                            <>
                                {(router.locale == 'au' || router.locale == 'international') && (
                                    <p className="text-white text-[13px] leading-[12px] md:mt-[38px] xsm:mt-[17px] text-[#054675] md:block md:text-left xsm:text-justify">
                                        <span className="font-semibold"> *T&C’s: </span>
                                        Discount is only available for new customers who haven’t made any previous purchases and is limited to their first purchase only. Discount can only be used once per customer, in one transaction. Discount only available for customers that register an account (no guest checkout) and subscribe to our newsletter. Discount is only available per person and can’t be split between multiple people. Discount applies to prescription and non-prescription glasses, frames, temples and Dresden branded accessories only. Discount is not applicable to contact lenses, optometry services, or non-Dresden accessories. Offer can’t be used in conjunction with any other campaigns, promotions or discounts. Add items to cart and use code: Welcome50. Free shipping on online orders only, with a minimum spend of $125. Available in-store, online and over the phone.
                                    </p>
                                )}

                                {(router.locale == 'nz' || router.locale == 'ca') && (
                                    <p className="text-white text-[13px] leading-[12px] md:mt-[38px] xsm:mt-[17px] text-[#054675] md:block md:text-left xsm:text-justify">
                                        <span className="font-semibold"> *T&C’s: </span>
                                        Discount is only available for new customers who haven’t made any previous purchases and is limited to their first purchase only. Discount can only be used once per customer, in one transaction. Discount only available for customers that register an account (no guest checkout) and subscribe to our newsletter. Discount is only available per person and can’t be split between multiple people. Discount applies to prescription and non-prescription glasses, frames, temples and Dresden branded accessories only. Discount is not applicable to contact lenses, optometry services, or non-Dresden accessories. Offer can’t be used in conjunction with any other campaigns, promotions or discounts. Add items to cart and use code: Welcome50. Free shipping on online orders only, with a minimum spend of $125. Available in-store, online and over the phone.                                    </p>
                                )}
                            </>
                        )}

                        {(isMobile) && (
                            <>
                                {(router.locale == 'au' || router.locale == 'international') && (
                                    <div className="flex flex-col items-center justify-center ">
                                        <p className="w-[350px] text-white text-[12px] leading-[12px] md:mt-[38px] xsm:mt-[30px] text-[#054675] md:block md:text-left xsm:text-center">
                                            <span className="font-semibold"> *T&C’s: </span>
                                            Discount is only available for new customers who haven’t made any previous purchases and is limited to their first purchase only. Discount can only be used once per customer, in one transaction. Discount only available for customers that register an account (no guest checkout) and subscribe to our newsletter. Discount is only available per person and can’t be split between multiple people. Discount applies to prescription and non-prescription glasses, frames, temples and Dresden branded accessories only. Discount is not applicable to contact lenses, optometry services, or non-Dresden accessories. Offer can’t be used in conjunction with any other campaigns, promotions or discounts. Add items to cart and use code: Welcome50. Free shipping on online orders only, with a minimum spend of $125. Available in-store, online and over the phone.                                        </p>
                                    </div>
                                )}

                                {(router.locale == 'nz' || router.locale == 'ca') && (
                                    <div className="flex flex-col items-center justify-center ">
                                        <p className="w-[350px] text-white text-[12px] leading-[12px] md:mt-[38px] xsm:mt-[30px] text-[#054675] md:block md:text-left xsm:text-center">
                                            <span className="font-semibold"> *T&C’s: </span>
                                            Discount is only available for new customers who haven’t made any previous purchases and is limited to their first purchase only. Discount can only be used once per customer, in one transaction. Discount only available for customers that register an account (no guest checkout) and subscribe to our newsletter. Discount is only available per person and can’t be split between multiple people. Discount applies to prescription and non-prescription glasses, frames, temples and Dresden branded accessories only. Discount is not applicable to contact lenses, optometry services, or non-Dresden accessories. Offer can’t be used in conjunction with any other campaigns, promotions or discounts. Add items to cart and use code: Welcome50. Free shipping on online orders only, with a minimum spend of $125. Available in-store, online and over the phone.                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                </div>
            </div>
            <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
        </section >
    </>;
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {

    const seoMetaTags = await getSeoMetaTags('/Welcome50', locale);

    return {
        props: {
            seoMetaTags
        }
    };
};